import {
  ZatcaStatus,
  ZatcaError,
  ZatcaWarning,
  ApiResponse
} from '../../types/api'
import { apiClient, getTenantApiUrl, getAuthToken } from '../../utils/api'

export class ZatcaService {
  private readonly basePath = '/zatca'

  // QR Code Generation
  async generateQRCode(invoiceId: string, options?: {
    version?: 1 | 2
    format?: 'base64' | 'svg' | 'png'
    size?: number
  }): Promise<ApiResponse<{
    qr_code: string
    format: string
    version: number
    data_encoded: string
  }>> {
    return apiClient.post(`${this.basePath}/qr-generate`, {
      invoice_id: invoiceId,
      ...options
    })
  }

  async validateQRCode(qrData: string): Promise<ApiResponse<{
    valid: boolean
    version: number
    seller_name?: string
    tax_number?: string
    timestamp?: string
    total_amount?: number
    tax_amount?: number
    errors: string[]
  }>> {
    return apiClient.post(`${this.basePath}/qr-validate`, { qr_data: qrData })
  }

  // Invoice Compliance
  async validateInvoice(invoiceId: string): Promise<ApiResponse<{
    compliant: boolean
    errors: ZatcaError[]
    warnings: ZatcaWarning[]
    compliance_score: number
    required_fields: Array<{
      field: string
      status: 'valid' | 'invalid' | 'missing'
      message?: string
    }>
  }>> {
    return apiClient.post(`${this.basePath}/validate-invoice`, { invoice_id: invoiceId })
  }

  async getComplianceStatus(invoiceId: string): Promise<ApiResponse<ZatcaStatus>> {
    return apiClient.get(`${this.basePath}/compliance-status/${invoiceId}`)
  }

  async generateCompliantHash(invoiceId: string): Promise<ApiResponse<{
    hash: string
    algorithm: string
    invoice_xml?: string
  }>> {
    return apiClient.post(`${this.basePath}/generate-hash`, { invoice_id: invoiceId })
  }

  // E-Invoice Submission
  async submitToZatca(invoiceId: string, options?: {
    clearance?: boolean
    reporting?: boolean
    test_mode?: boolean
  }): Promise<ApiResponse<{
    success: boolean
    uuid?: string
    submission_date: string
    clearance_status?: 'CLEARED' | 'NOT_CLEARED' | 'REPORTED'
    validation_results?: {
      status: 'PASS' | 'FAIL' | 'WARNING'
      errors: ZatcaError[]
      warnings: ZatcaWarning[]
    }
    response_xml?: string
  }>> {
    return apiClient.post(`${this.basePath}/submit`, {
      invoice_id: invoiceId,
      ...options
    })
  }

  async getSubmissionStatus(submissionId: string): Promise<ApiResponse<{
    status: 'PENDING' | 'SUBMITTED' | 'CLEARED' | 'REJECTED'
    submitted_at?: string
    cleared_at?: string
    rejected_at?: string
    rejection_reason?: string
    zatca_uuid?: string
  }>> {
    return apiClient.get(`${this.basePath}/submission-status/${submissionId}`)
  }

  async resubmitInvoice(invoiceId: string): Promise<ApiResponse<{
    success: boolean
    new_submission_id: string
  }>> {
    return apiClient.post(`${this.basePath}/resubmit`, { invoice_id: invoiceId })
  }

  // Certificate Management
  async uploadCertificate(data: {
    certificate: File
    private_key: File
    password?: string
    environment: 'sandbox' | 'production'
  }): Promise<ApiResponse<{
    certificate_id: string
    valid_from: string
    valid_to: string
    serial_number: string
    issuer: string
  }>> {
    const formData = new FormData()
    formData.append('certificate', data.certificate)
    formData.append('private_key', data.private_key)
    formData.append('environment', data.environment)
    
    if (data.password) {
      formData.append('password', data.password)
    }
    
    return apiClient.post(`${this.basePath}/certificate/upload`, formData)
  }

  async getCertificateInfo(): Promise<ApiResponse<{
    certificate_id?: string
    serial_number?: string
    issuer?: string
    subject?: string
    valid_from?: string
    valid_to?: string
    is_valid: boolean
    days_until_expiry?: number
    environment: 'sandbox' | 'production'
  }>> {
    return apiClient.get(`${this.basePath}/certificate/info`)
  }

  async validateCertificate(): Promise<ApiResponse<{
    valid: boolean
    errors: string[]
    warnings: string[]
    expiry_status: 'valid' | 'expiring_soon' | 'expired'
  }>> {
    return apiClient.post(`${this.basePath}/certificate/validate`)
  }

  // Configuration
  async getConfiguration(): Promise<ApiResponse<{
    environment: 'sandbox' | 'production'
    api_url: string
    certificate_configured: boolean
    auto_submit: boolean
    auto_clearance: boolean
    compliance_enabled: boolean
    settings: Record<string, any>
  }>> {
    return apiClient.get(`${this.basePath}/configuration`)
  }

  async updateConfiguration(config: {
    environment?: 'sandbox' | 'production'
    auto_submit?: boolean
    auto_clearance?: boolean
    compliance_enabled?: boolean
    settings?: Record<string, any>
  }): Promise<ApiResponse<void>> {
    return apiClient.put(`${this.basePath}/configuration`, config)
  }

  async testConnection(): Promise<ApiResponse<{
    connected: boolean
    response_time: number
    api_version: string
    environment: string
    errors: string[]
  }>> {
    return apiClient.post(`${this.basePath}/test-connection`)
  }

  // Reporting and Analytics
  async getSubmissionHistory(filters?: {
    date_from?: string
    date_to?: string
    status?: string
    limit?: number
  }): Promise<ApiResponse<Array<{
    id: string
    invoice_id: string
    invoice_number: string
    status: string
    submitted_at: string
    cleared_at?: string
    errors: ZatcaError[]
    warnings: ZatcaWarning[]
  }>>> {
    return apiClient.get(`${this.basePath}/submission-history`, filters)
  }

  async getComplianceReport(period?: {
    date_from?: string
    date_to?: string
  }): Promise<ApiResponse<{
    total_invoices: number
    compliant_invoices: number
    non_compliant_invoices: number
    compliance_rate: number
    common_errors: Array<{
      error_code: string
      error_message: string
      count: number
    }>
    monthly_compliance: Array<{
      month: string
      total: number
      compliant: number
      rate: number
    }>
  }>> {
    return apiClient.get(`${this.basePath}/compliance-report`, period)
  }

  async getErrorStatistics(): Promise<ApiResponse<{
    total_errors: number
    resolved_errors: number
    pending_errors: number
    error_breakdown: Array<{
      code: string
      message: string
      count: number
      severity: 'error' | 'warning'
      resolution_rate: number
    }>
  }>> {
    return apiClient.get(`${this.basePath}/error-statistics`)
  }

  // Bulk Operations
  async bulkValidate(invoiceIds: string[]): Promise<ApiResponse<{
    total: number
    validated: number
    failed: number
    results: Array<{
      invoice_id: string
      compliant: boolean
      errors: ZatcaError[]
      warnings: ZatcaWarning[]
    }>
  }>> {
    return apiClient.post(`${this.basePath}/bulk-validate`, { invoice_ids: invoiceIds })
  }

  async bulkSubmit(invoiceIds: string[], options?: {
    clearance?: boolean
    test_mode?: boolean
  }): Promise<ApiResponse<{
    total: number
    submitted: number
    failed: number
    results: Array<{
      invoice_id: string
      success: boolean
      uuid?: string
      error?: string
    }>
  }>> {
    return apiClient.post(`${this.basePath}/bulk-submit`, {
      invoice_ids: invoiceIds,
      ...options
    })
  }

  // XML Operations
  async generateXML(invoiceId: string): Promise<ApiResponse<{
    xml: string
    ubl_version: string
    schema_valid: boolean
    validation_errors: string[]
  }>> {
    return apiClient.post(`${this.basePath}/generate-xml`, { invoice_id: invoiceId })
  }

  async validateXML(xml: string): Promise<ApiResponse<{
    valid: boolean
    schema_valid: boolean
    zatca_compliant: boolean
    errors: string[]
    warnings: string[]
  }>> {
    return apiClient.post(`${this.basePath}/validate-xml`, { xml })
  }

  async downloadXML(invoiceId: string): Promise<Response> {
    const response = await fetch(`${getTenantApiUrl()}${this.basePath}/download-xml/${invoiceId}`, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    })
    
    if (!response.ok) {
      throw new Error('XML download failed')
    }
    
    return response
  }

  // System Health
  async getSystemHealth(): Promise<ApiResponse<{
    zatca_api_status: 'online' | 'offline' | 'degraded'
    certificate_status: 'valid' | 'expiring' | 'expired' | 'missing'
    last_successful_submission?: string
    pending_submissions: number
    failed_submissions_last_24h: number
    system_load: 'low' | 'medium' | 'high'
  }>> {
    return apiClient.get(`${this.basePath}/system-health`)
  }

  async getMaintenanceSchedule(): Promise<ApiResponse<{
    scheduled_maintenance: Array<{
      start_time: string
      end_time: string
      description: string
      impact: 'low' | 'medium' | 'high'
    }>
    next_maintenance?: string
  }>> {
    return apiClient.get(`${this.basePath}/maintenance-schedule`)
  }
}

// Create and export service instance
export const zatcaService = new ZatcaService() 